import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWorkspace } from '../../contexts/WorkspaceContext';

/*
 * WorkspaceSwitcher — compact pill in the header that shows the active
 * workspace and opens a dropdown listing every workspace the user can
 * jump to (Personal + each Organization they belong to).
 *
 * Hidden entirely when the `business_workspaces` feature flag is off OR
 * the user belongs to zero organizations — keeps the personal-only UX
 * clean for users who don't care about teams.
 */

const WorkspaceSwitcher = () => {
  const { activeWorkspace, workspaces, switchWorkspace, isBusinessEnabled } = useWorkspace();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [open]);

  // Hide if business isn't enabled or the user has no orgs yet.
  if (!isBusinessEnabled) return null;
  if (workspaces.length <= 1) return null;

  const isOrg = activeWorkspace.kind === 'org';

  return (
    <div className="relative" ref={ref} data-testid="workspace-switcher">
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid="workspace-switcher-button"
        aria-haspopup="menu"
        aria-expanded={open}
        className="inline-flex items-center gap-1.5 max-w-[180px] px-2.5 py-1.5 rounded-full bg-white/90 hover:bg-white border border-gray-200 shadow-sm text-sm font-medium text-gray-800 transition-colors"
        title="Switch workspace"
      >
        <span className="text-base" aria-hidden="true">
          {isOrg ? '🏢' : '👤'}
        </span>
        <span className="truncate">{activeWorkspace.name}</span>
        <svg className="w-3.5 h-3.5 text-gray-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 w-64 bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden z-50"
          role="menu"
          data-testid="workspace-switcher-menu"
        >
          <ul className="py-1 max-h-72 overflow-auto">
            {workspaces.map((w) => {
              const active = w.id === activeWorkspace.id;
              return (
                <li key={w.id}>
                  <button
                    onClick={() => { switchWorkspace(w); setOpen(false); }}
                    data-testid={`workspace-option-${w.id}`}
                    className={`w-full flex items-center justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-gray-50 ${active ? 'bg-indigo-50' : ''}`}
                  >
                    <span className="flex items-center gap-2 min-w-0">
                      <span className="text-base" aria-hidden="true">
                        {w.kind === 'org' ? '🏢' : '👤'}
                      </span>
                      <span className="truncate">{w.name}</span>
                    </span>
                    {active && (
                      <svg className="w-4 h-4 text-indigo-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
          <div className="border-t border-gray-100 py-1">
            <button
              onClick={() => { setOpen(false); navigate('/settings/subscription'); }}
              data-testid="workspace-create-cta"
              className="w-full text-left px-3 py-2 text-sm text-indigo-600 hover:bg-indigo-50 font-medium"
            >
              + Create another Business workspace
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkspaceSwitcher;
